import React from 'react';
import { Chart as ChartJS, RadialLinearScale, PointElement, LineElement, Filler, Tooltip, Legend } from 'chart.js';
import { Radar } from 'react-chartjs-2';

ChartJS.register(RadialLinearScale, PointElement, LineElement, Filler, Tooltip, Legend);

const ENTRY_COLOR = '#1086a4';
const EXIT_COLOR = '#f59e0b';

const fmt = (n) => (n === null || n === undefined ? '–' : Number(n).toFixed(1));

const Delta = ({ value }) => {
  if (value === null || value === undefined) return <span className="text-gray-400">–</span>;
  const cls = value > 0 ? 'text-green-600 bg-green-50' : value < 0 ? 'text-red-600 bg-red-50' : 'text-gray-500 bg-gray-100';
  return <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${cls}`}>{value > 0 ? '+' : ''}{Number(value).toFixed(1)}</span>;
};

// Splits long competency names over two lines so they fit around the chart
const wrapLabel = (text, max = 14) => {
  const lines = [];
  let line = '';
  String(text).split(' ').forEach(word => {
    if ((line + ' ' + word).trim().length > max && line) { lines.push(line); line = word; }
    else line = (line + ' ' + word).trim();
  });
  if (line) lines.push(line);
  return lines.length > 1 ? lines : text;
};

/**
 * Spider-chart results for an Entry / Exit survey.
 * results: { competencies: [{ name, entry, exit, change }], overall, scaleMax, strongestGrowth }
 */
const SurveyResults = ({ results, firstName }) => {
  const { competencies, overall, scaleMax, strongestGrowth } = results;
  const hasExit = overall.exit !== null;
  const cardClass = 'bg-white dark:bg-card border border-gray-200 dark:border-border rounded-2xl shadow-sm';

  const datasets = [{
    label: 'Entry',
    data: competencies.map(c => c.entry),
    borderColor: ENTRY_COLOR,
    backgroundColor: 'rgba(16, 134, 164, 0.15)',
    pointBackgroundColor: ENTRY_COLOR,
    borderWidth: 2,
    pointRadius: 4
  }];
  if (hasExit) {
    datasets.push({
      label: 'Exit',
      data: competencies.map(c => c.exit),
      borderColor: EXIT_COLOR,
      backgroundColor: 'rgba(245, 158, 11, 0.15)',
      pointBackgroundColor: EXIT_COLOR,
      borderWidth: 2.5,
      pointRadius: 5
    });
  }

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: true,
    plugins: {
      legend: { display: false },
      tooltip: { callbacks: { label: (ctx) => `${ctx.dataset.label}: ${fmt(ctx.raw)} / ${scaleMax}` } }
    },
    scales: {
      r: {
        min: 0,
        max: scaleMax,
        beginAtZero: true,
        ticks: { stepSize: scaleMax <= 10 ? 1 : undefined, backdropColor: 'transparent', color: '#9ca3af', font: { size: 11 } },
        grid: { color: 'rgba(156, 163, 175, 0.3)' },
        angleLines: { color: 'rgba(156, 163, 175, 0.3)' },
        pointLabels: { color: '#374151', font: { size: 13, weight: '600' } }
      }
    }
  };

  const title = hasExit
    ? `${firstName ? `${firstName}, look` : 'Look'} how far you’ve come`
    : `${firstName ? `${firstName}’s` : 'Your'} starting point`;

  return (
    <div>
      <p className="text-sm text-gray-500 dark:text-foreground/60 mb-1">{hasExit ? 'Entry vs Exit' : 'Entry Survey results'}</p>
      <h1 className="text-3xl sm:text-4xl font-extrabold text-gray-900 dark:text-foreground mb-6">{title}</h1>

      {competencies.length === 0 ? (
        <div className={`${cardClass} p-8 text-center text-gray-500 dark:text-foreground/60`}>
          Your answers are saved. This survey has no competencies, so there is no chart to show.
        </div>
      ) : (
        <div className={`${cardClass} p-5 sm:p-8`}>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-start">
            {/* Spider chart */}
            <div>
              <div className="max-w-sm mx-auto">
                <Radar
                  data={{ labels: competencies.map(c => wrapLabel(c.name)), datasets }}
                  options={chartOptions}
                  aria-label="Spider chart of your scores by competency"
                  role="img"
                />
              </div>
              <div className="flex justify-center gap-5 mt-3 text-sm text-gray-600 dark:text-foreground/70">
                <span className="flex items-center gap-2"><i className="w-3 h-3 rounded-sm inline-block" style={{ background: ENTRY_COLOR }} />Entry</span>
                {hasExit && <span className="flex items-center gap-2"><i className="w-3 h-3 rounded-sm inline-block" style={{ background: EXIT_COLOR }} />Exit</span>}
              </div>
            </div>

            {/* Scores */}
            <div>
              <div className="flex flex-wrap gap-6 pb-5 mb-5 border-b border-gray-100 dark:border-border">
                <div>
                  <p className="text-4xl font-extrabold text-gray-900 dark:text-foreground">{fmt(overall.entry)}<span className="text-base font-semibold text-gray-400"> / {scaleMax}</span></p>
                  <p className="text-sm text-gray-500 dark:text-foreground/60">Entry average</p>
                </div>
                {hasExit && (
                  <>
                    <div>
                      <p className="text-4xl font-extrabold text-gray-900 dark:text-foreground">{fmt(overall.exit)}<span className="text-base font-semibold text-gray-400"> / {scaleMax}</span></p>
                      <p className="text-sm text-gray-500 dark:text-foreground/60">Exit average</p>
                    </div>
                    <div>
                      <p className="text-4xl font-extrabold"><Delta value={overall.change} /></p>
                      <p className="text-sm text-gray-500 dark:text-foreground/60 mt-2">Overall growth</p>
                    </div>
                  </>
                )}
              </div>

              <h3 className="font-bold text-gray-900 dark:text-foreground mb-3">By competency</h3>
              <div className="space-y-4">
                {competencies.map(c => (
                  <div key={c._id}>
                    <div className="flex items-center justify-between gap-3 mb-1.5">
                      <span className="font-semibold text-gray-800 dark:text-foreground">{c.name}</span>
                      <span className="text-sm text-gray-700 dark:text-foreground/80 flex items-center gap-2">
                        {hasExit ? <>{fmt(c.entry)} → <strong>{fmt(c.exit)}</strong> <Delta value={c.change} /></> : <strong>{fmt(c.entry)}</strong>}
                      </span>
                    </div>
                    <div className="relative h-2.5 bg-gray-100 dark:bg-muted rounded-full overflow-hidden">
                      {c.entry !== null && (
                        <div className="absolute inset-y-0 left-0 rounded-full" style={{ width: `${(c.entry / scaleMax) * 100}%`, background: hasExit ? 'rgba(16, 134, 164, 0.45)' : ENTRY_COLOR }} />
                      )}
                      {hasExit && c.exit !== null && (
                        <div className="absolute left-0 bottom-0 h-1 rounded-full" style={{ width: `${(c.exit / scaleMax) * 100}%`, background: EXIT_COLOR }} />
                      )}
                    </div>
                  </div>
                ))}
              </div>

              {hasExit ? (
                <div className="mt-6 p-4 rounded-xl bg-green-50 dark:bg-green-900/20">
                  <h3 className="font-bold text-gray-900 dark:text-foreground mb-2">
                    {strongestGrowth.length ? 'Strongest areas of growth' : 'Your growth'}
                  </h3>
                  {strongestGrowth.length ? (
                    <ol className="list-decimal list-inside space-y-1 text-sm text-gray-700 dark:text-foreground/80">
                      {strongestGrowth.map(g => (
                        <li key={g._id}><strong>{g.name}</strong>: {fmt(g.entry)} → {fmt(g.exit)} <Delta value={g.change} /></li>
                      ))}
                    </ol>
                  ) : (
                    <p className="text-sm text-gray-700 dark:text-foreground/80">Your scores held steady. Look at the competencies above to decide where to focus next.</p>
                  )}
                </div>
              ) : (
                <p className="text-sm text-gray-500 dark:text-foreground/60 mt-6">
                  Scores are the average of your answers for each competency. You’ll answer the same questions again in the Exit Survey and see how far you’ve come.
                </p>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SurveyResults;
